import React, { useState, useRef, useEffect } from 'react';
import { Box, Container, Typography, IconButton } from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload'; 
import DeleteIcon from '@mui/icons-material/Delete';
import Heading from '../../Components/heading.tsx';
import { getDocuments, addDocuments, deleteDocument } from './action.ts';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import imageCompression from 'browser-image-compression';

const styles = {
  statusContainer: {
    backgroundColor: '#f0f0f0', 
    paddingBottom: '16px',
    borderRadius: '8px',
    width: '100%',
    marginTop: '30px',
  },
  documentBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: '8px',
    padding: '10px',
    marginBottom: '10px',
  },
  documentContainer: {
    backgroundColor: '#f0f0f0',
    paddingRight: '0px',
    paddingLeft: '0px',
  },
  inputFile: {
    display: 'none',
  },
  actionContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  viewLabel: {
    marginRight: '8px',
    color: '#02e8ee',
    cursor: 'pointer',
  },
  iframeContainer: {
    width: '100vw', 
    height: '100vh',
    border: 'none',
    overflow: 'hidden',
  },
  backButton: {
    marginTop: '25px',
    marginLeft:'5px',
    color: 'black',
  }
};

const DocumentsScreen: React.FC = () => {
  const documentMapping: { [key: string]: string } = {
    'aadhar': 'Aadhar Card',
    'drivers license': 'Driving License',
    'pancard': 'PAN Card', 
    'registration certificate': 'Registration Certificate',
    'pollution certificate': 'Pollution Certificate',
    'fitness certificate': 'Fitness Certificate',
    'insurance': 'Insurance',
  };
  const getDocumentMapping: { [key: string]: string } = {
    'aadhar': 'Aadhar Card',
    'driving license': 'Driving License',
    'pancard': 'PAN Card',
    'registration certificate': 'Registration Certificate',
    'pollution certificate': 'Pollution Certificate',
    'fitness certificate': 'Fitness Certificate',
    'insurance': 'Insurance',
  };
  const [uploadedUserFiles, setUploadedUserFiles] = useState<{ [key: string]: { name: string; location: string; id: number | null} | null }>({});
  const [uploadedVehicleFiles, setUploadedVehicleFiles] = useState<{ [key: string]: { name: string; location: string; id: number | null } | null }>({});
  const [isViewingDocument, setIsViewingDocument] = useState(false);
  const [documentUrl, setDocumentUrl] = useState('');
  const [isUploading, setIsUploading] = useState<{ [key: string]: boolean }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentDocument, setCurrentDocument] = useState<string>('');
  const navigate = useNavigate();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    let file = event.target.files?.[0];
    if (file && currentDocument) {
      setIsUploading(prev => ({ ...prev, [currentDocument]: true }));

      // Compress if size > 2 MB
      if (file.size / 1024 / 1024 > 2 && file.type.startsWith('image/')) {
        try {
          const options = {
            maxSizeMB: 2,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          };
          const compressedBlob = await imageCompression(file, options);

          // Convert Blob back to File
          file = new File([compressedBlob], file.name, { type: file.type });
        } catch (err) {
          console.error('Image compression error:', err);
          toast.error('Failed to compress image. Uploading original file.');
        }
      }
      if (currentDocument === 'aadhar' || currentDocument === 'drivers license' || currentDocument === 'pancard') {
        setUploadedUserFiles(prev => ({ 
          ...prev, 
          [currentDocument]: { name: file.name, location: '', id: null } 
        }));
      } else {
        setUploadedVehicleFiles(prev => ({ 
          ...prev, 
          [currentDocument]: { name: file.name, location: '', id: null } 
        }));
      }

      handleUpload(currentDocument, file);
    }
  };

  const handleUpload = (document: string, file: File) => {
    const isUserDocument = document === 'driving license' || document === 'aadhar' ||document === 'pancard'; 
    
    const successCallback = (response: any) => {
      setIsUploading(prev => ({ ...prev, [document]: false }));
      
      if (isUserDocument) {
        setUploadedUserFiles(prevFiles => ({
          ...prevFiles,
          [document]: { 
            name: file.name, 
            location: response.message.location, 
            id: response.message.id 
          }
        }));
      } else {
        setUploadedVehicleFiles(prevFiles => ({
          ...prevFiles,
          [document]: { 
            name: file.name, 
            location: response.message.location, 
            id: response.message.id 
          }
        }));
      }
      
      toast.success(`${isUserDocument ? getDocumentMapping[document] : documentMapping[document]} uploaded successfully`);
    };

    const errorCallback = (errMessage: any) => {
      setIsUploading(prev => ({ ...prev, [document]: false }));
            if (isUserDocument) {
        setUploadedUserFiles(prev => ({ ...prev, [document]: null }));
      } else {
        setUploadedVehicleFiles(prev => ({ ...prev, [document]: null }));
      }
      
      toast.error(errMessage?.message || "An unexpected error occurred.");
    };

    if (isUserDocument) {
      addDocuments(
        document === 'driving license' ? file : null,
        document === 'aadhar' ? file : null,
        null, null, null, null,
        '', userId,
        document === 'pancard' ? file : null, 
        successCallback,
        errorCallback
      );
    } else {
      addDocuments(
        null, null,
        document === 'registration certificate' ? file : null,
        document === 'insurance' ? file : null,
        document === 'fitness certificate' ? file : null,
        document === 'pollution certificate' ? file : null,
        Number(vehicleId),
          userId,
          null,                
        successCallback,
        errorCallback
      );
    }
  };

const askCameraPermission = async () => {
  try {
    await navigator.mediaDevices.getUserMedia({ video: true });
    return true;
  } catch (err) {
    toast.error("Camera permission denied");
    return false;
  }
};

const handleUploadClick = async (document: string) => {
  setCurrentDocument(document);

  const granted = await askCameraPermission();
  if (!granted) return;

  if (fileInputRef.current) {
    fileInputRef.current.value = '';
    fileInputRef.current.click();
  }
};

  const handleDelete = (documentKey: string) => {
    const usersDocumentDetails = uploadedUserFiles[documentKey];
    const vehiclesDocumentDetails = uploadedVehicleFiles[documentKey];
    if (!usersDocumentDetails?.id && !vehiclesDocumentDetails?.id) {
      toast.error('Document not found or invalid.');
      return;
    }
    const userDocumentId = usersDocumentDetails?.id;
    const vehicleDocumentId = vehiclesDocumentDetails?.id;
    deleteDocument(
      userDocumentId,
      vehicleDocumentId,
      vehicleId,
      userId,
      (response) => {
        toast.success(response?.message);
        setUploadedUserFiles((prev) => ({ ...prev, [documentKey]: null }));
        setUploadedVehicleFiles((prev) => ({ ...prev, [documentKey]: null }));
      },
      (errorMessage) => {
        toast.error(errorMessage.data.message);
      }
    );
  };

  const handleBack = () => {
    navigate(`/wv/dashboard?user_name=${userName}&vehicle_id=${vehicleId}&user_id=${userId}&token=${token}`);
  }

  const handleDocumentBack = () => {
    setIsViewingDocument(false);
  };
  
  const params = new URLSearchParams(window.location.search);
  const vehicleId = params.get('vehicle_id');
  const userId = params.get('user_id');
  const token= params.get('token') || '';
  const userName = params.get('user_name') || '';

  useEffect(() => {
    getDocuments(
      vehicleId,
      (response: any) => {
        const userDocumentDetails = response.message?.user_documents || [];
        const uploadedUserDocuments: { [key: string]: { id: number; name: string; location: string } } = {};
        const vehicleDocumentDetails = response.message?.vehicle_documents || [];
        const uploadedVehicleDocuments: { [key: string]: { id: number; name: string; location: string } } = {};

        userDocumentDetails.forEach(doc => {
          if (doc.status === 'uploaded') {
            uploadedUserDocuments[doc.document_name] = {
              id: doc.id,
              name: getDocumentMapping[doc.document_name] || doc.document_name,
              location: doc.location,
            };
          }
        });

        vehicleDocumentDetails.forEach(doc => {
          if (doc.status === 'uploaded') {
            uploadedVehicleDocuments[doc.document_name] = {
              id: doc.id,
              name: documentMapping[doc.document_name] || doc.document_name,
              location: doc.location,
            };
          }
        });

        setUploadedUserFiles(uploadedUserDocuments);
        setUploadedVehicleFiles(uploadedVehicleDocuments);
      },
      (errMessage: any) => {
        toast.error(errMessage);
      }
    );
  }, [vehicleId]);
  
  const handleViewClick = (document: string, location: string) => {
    const url = `${global.documents_image_url}${location}`;
    setDocumentUrl(url);
    setIsViewingDocument(true);
  };

  return (
    <Container style={styles.documentContainer}>
      {isViewingDocument ? (
        <Box>
          <IconButton edge="start" color="inherit" aria-label="back" onClick={handleDocumentBack} style={styles.backButton}>
            <ArrowBackIcon />
          </IconButton>
          <iframe src={documentUrl} style={styles.iframeContainer} title="Document Viewer" />
        </Box>
      ) : (
        <Box>
          <Box style={styles.statusContainer}>
            <Heading title="Documents" onBackClick={handleBack} />
          </Box>
          <Box sx={{ paddingLeft: '16px', paddingRight: '16px' }}>
          <input
              ref={fileInputRef}
              type="file"
              style={styles.inputFile}
              onChange={handleFileChange}
              accept="image/*,application/pdf"
            />
            {Object.keys(getDocumentMapping).map((key, index) => {
              const hasFile = !!uploadedUserFiles[key] || !!uploadedVehicleFiles[key];
              const isUploadingDoc = isUploading[key];
              
              return (
                <Box key={index} style={styles.documentBox}>
                  <Typography variant="body1">{getDocumentMapping[key]}</Typography>
                  <div style={styles.actionContainer}>
                    {!hasFile || isUploadingDoc ? (
                      <IconButton 
                        onClick={() => handleUploadClick(key)}
                        disabled={isUploadingDoc}
                      >
                        <UploadIcon />
                        {isUploadingDoc && ' Uploading...'}
                      </IconButton>
                    ) : (
                      <div style={styles.actionContainer}>
                        <Typography 
                          variant="body1" 
                          style={styles.viewLabel} 
                          onClick={() => handleViewClick(
                            key, 
                            uploadedUserFiles[key]?.location || uploadedVehicleFiles[key]?.location
                          )}
                        >
                          View
                        </Typography>
                        <IconButton onClick={() => handleDelete(key)}>
                          <DeleteIcon />
                        </IconButton>
                      </div>
                    )}
                  </div>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default DocumentsScreen;